import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import TableLayout from "../../components/TableLayout";
import { CommonSelect, CstmPagination } from "../../components/Common/Common";
import { useNavigate } from "react-router-dom";
import TableHeader from "components/shared/FormComponents/TableHeader";
import { CLIENT_REPORT_TYPES, CLIENT_TYPE, CLIENT_TYPES, GRADE_OPTIONS, REPORT_IDS } from "config/constants";
import { useLazyGetSchoolClientsListQuery } from "store/services/clients/school.client";
import { useLazyGetHomeEducationListQuery } from "store/services/clients/homeEducation";
import { useLazyGetChatterClientsListQuery } from "store/services/clients/chatter";
import { useLazyGetPrivateClientListQuery } from "store/services/clients/private";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { MY_FAVOURITE_GOAL } from "config/paths";
import { useSchoolLookupQuery } from "store/services/utility";

const MyFavouriteGoal = () => {

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState({ clientType: "school", search: "", sort: { column: "id", direction: "desc" } });
  const [clientsList, setClientsList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  const [getSchoolClients, { isLoading: getSchoolClientsLoading, isFetching: getSchoolClientsFetching, isError: getSchoolClientsError }] = useLazyGetSchoolClientsListQuery();
  const [getHomeEducationClients, { isLoading: getHomeEducationClientsLoading, isFetching: getHomeEducationClientsFetching, isError: getHomeEducationClientsError }] = useLazyGetHomeEducationListQuery();
  const [getPrivateClients, { isLoading: getPrivateClientsLoading, isFetching: getPrivateClientsFetching, isError: getPrivateClientsError }] = useLazyGetPrivateClientListQuery();
  const [getChatterClients, { isLoading: getChatterClientsLoading, isFetching: getChatterClientsFetching, isError: getChatterClientsError }] = useLazyGetChatterClientsListQuery();

  const { data: schoolList, isLoading: schoolListLoading, isFetching: schoolListFetching } = useSchoolLookupQuery();
  const schoolListOptions = schoolList?.map((i) => ({ value: i.id, label: i.name }));

  // Report Path
  const assessmentId = REPORT_IDS["INDIVIDUAL_SESSION_REPORT"];
  const report = CLIENT_REPORT_TYPES[assessmentId];
  const clientType = filter?.clientType;
  const path = report?.path?.replace(":clientType", clientType)?.replace(":assessmentId", assessmentId)?.replace("/:clientId", "");

  const getClientsList = async () => {
    try {
      let result = { error: true };

      switch (filter?.clientType) {

        case "school":
          result = await getSchoolClients({ page: currentPage, limit: pageSize, ...filter });
          break;

        case "home_education":
          result = await getHomeEducationClients({ page: currentPage, limit: pageSize, ...filter });
          break;

        case "chatter":
          result = await getChatterClients({ page: currentPage, limit: pageSize, ...filter });
          break;

        case "private":
          result = await getPrivateClients({ page: currentPage, limit: pageSize, ...filter });
          break;

        default: result = { error: true };
          break;
      };

      if (result?.data) {

        setClientsList(result?.data?.records);
        const totalRecords = result?.data?.totalRecords || 0
        setTotalPages(Math.ceil(totalRecords / pageSize));
      };

      return;

    } catch (error) { console.log("Error getting favourite sessions") };
  };

  const resetFilter = () => {
    setFilter({ clientType: "school", search: "", sort: { column: "id", direction: "desc" } });
    setCurrentPage(1)
  };

  const handleFilterChange = (key) => (value) => {

    setCurrentPage(1);
    setFilter((prevState) => ({ ...prevState, [key]: key == "grade" ? value?.value : value, }));
  };

  const handlePageChange = (page) => {
    window.scrollTo(0, 0);
    setCurrentPage(page);
  };

  const column = [
    {
      head: "Last Name",
      accessor: "LastName",
      isComponent: true,
      component: (item, ind) => (
        <div className="d-flex align-items-center gap-10">
          <h6 className="m-0 fw-sbold text-capitalize">{item?.lastname || "--"}</h6>
        </div>
      ),
    },
    {
      head: "First Name",
      accessor: "FirstName",
      component: (item, ind) => (
        <h6 className="m-0 fw-sbold text-capitalize">{item?.firstname || "--"}</h6>
      ),
    },
    {
      head: "Goals Name",
      accessor: "GoalsName",
      component: (item, ind) => (
        <div className="text-capitalize cell-text-ellipsis">
          {
            item?.goalList?.reduce((acc, item) => {
              return acc ? (acc + ", " + (item || "")) : item
            }, "") || "-"
          }
        </div>
      ),
    },
  ];

  useEffect(() => {
    getClientsList();
  }, [filter, currentPage]);

  return (
    <>
      <section className="designations position-relative py-4">
        <Container fluid>
          <Row>
            <Col lg="12" className="my-2">
              <TableHeader
                heading="My Favourite Sessions"
                filter={filter}
                handleFilterChange={handleFilterChange}
                searchPlaceholder={"Client First, Last Name"}
                resetFilter={resetFilter}
                filterComponent={
                  <>
                    <li>
                      <div className="d-flex align-items-center px-2">
                        <label>Client Type: </label>
                        <CommonSelect
                          placeholder={"Select Client Type"}
                          value={CLIENT_TYPES?.find(x => x.value == filter?.clientType)}
                          option={CLIENT_TYPES}
                          onChange={(e) => handleFilterChange("clientType")(e.value)}
                          isFilter
                        />
                      </div>
                    </li>
                    <li>
                      <CommonSelect
                        placeholder={"Select School"}
                        option={schoolListOptions}
                        onChange={(e) => handleFilterChange("school")(e)}
                        value={filter?.school}
                        isFilter
                        isLoading={schoolListLoading || schoolListFetching}
                      />
                    </li>
                    <li>
                      <CommonSelect
                        placeholder={"Select Grade"}
                        option={GRADE_OPTIONS}
                        onChange={(e) => handleFilterChange("grade")(e)}
                        value={filter?.grade}
                        isFilter
                      />
                    </li>
                  </>
                }
              />
              <div className="table-outer">
                {(getSchoolClientsLoading) ? (
                  <div style={{ height: "200px" }}>
                    <LoadingIndicator />
                  </div>
                ) : (getSchoolClientsError || getHomeEducationClientsError || getPrivateClientsError || getChatterClientsError) ? (
                  <ErrorComponent />
                ) : (
                  <>
                    {(getHomeEducationClientsLoading || getPrivateClientsLoading || getChatterClientsLoading || getSchoolClientsFetching || getHomeEducationClientsFetching || getPrivateClientsFetching || getChatterClientsFetching) && <LoadingOverlay />}
                    <TableLayout
                      data={clientsList || []}
                      column={column}
                      url={path}
                      state={{ previousUrl: MY_FAVOURITE_GOAL }}

                    />
                  </>
                )}
                <CstmPagination
                  totalPages={totalPages}
                  activePage={currentPage}
                  handlePageChange={handlePageChange}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default MyFavouriteGoal;
